import React from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  Pagination,
  Toggle,
} from "@hid-galaxy-ui/galaxy-react";
import moment from "moment";
import ServiceContractDetailModal from "../../../../../common/components/ServiceContractDetailModal";
import useProductListPageRoute from "../../../../../hooks/useProductListPageRoute";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import { ContractType } from "../../../../../redux/slices/my-account/Types";
import { getWorkPackagesSagaAction } from "../../../../../redux/saga/external-contract/saga-actions";
import "./TabContentContractStatus.scss";

const TabContentContractStatus: React.FC<{ tabKey: string }> = ({ tabKey }) => {
  const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const [showActiveOnly, setShowActiveOnly] = React.useState<boolean>(false);
  const { contracts } = useAppSelector((state) => state.my_account);
  const { workPackages } = useAppSelector((state) => state.external_contract);
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (!workPackages.length) dispatch(getWorkPackagesSagaAction());
  }, []);

  React.useEffect(() => {
    setCurrentPageNumber(1);
  }, [tabKey, showActiveOnly]);

  const handleToggleChange = (e: any) => {
    setShowActiveOnly(e.target.checked);
  };

  let filteredContracts: ContractType[] = [];
  if (tabKey === "direct-contracts") {
    filteredContracts = contracts.filter(
      (contract) => contract.source === "external"
    );
  } else {
    filteredContracts = contracts.filter(
      (contract) =>
        contract.collection?.trim().toLowerCase().replaceAll(" ", "_") ===
        tabKey
    );
  }

  if (showActiveOnly) {
    filteredContracts = filteredContracts.filter(
      (contract) => contract.status === "Active"
    );
  }

  const pageSize = 3;
  const totalRecords = filteredContracts.length;
  const handlePageChange = (clickedPageNumber: number | null) => {
    setCurrentPageNumber(clickedPageNumber);
  };
  const paginatedData = filteredContracts.slice(
    (currentPageNumber - 1) * pageSize,
    currentPageNumber * pageSize
  );

  return (
    <div className="contract-status-container">
      <div className="toggle-container">
        <Toggle
          id="contract-status-toggle"
          labelON="Active Contracts"
          labelOFF="All Contracts"
          onClick={handleToggleChange}
          toggled={showActiveOnly}
        />
      </div>
      {filteredContracts.length > 0 ? (
        <>
          {paginatedData.map((contractData) => (
            <CompactContractCard
              contract={contractData}
              key={contractData.ID}
              tabKey={tabKey}
            />
          ))}
          {filteredContracts.length > 3 && (
            <Pagination
              currentPageNumber={currentPageNumber}
              totalRecords={totalRecords}
              pageSize={pageSize}
              border={false}
              onPageNumberClick={handlePageChange}
            />
          )}
        </>
      ) : (
        <div className="no-contracts">No Contracts</div>
      )}
    </div>
  );
};

const CompactContractCard: React.FC<{
  contract: ContractType;
  tabKey: string;
}> = ({ contract, tabKey }) => {
  const [detailModalOpen, setDetailModalOpen] = React.useState<boolean>(false);
  const { collection } = useAppSelector((state) => state.plp);
  const { getRouteIndex } = useProductListPageRoute();
  const shopifyNavigationHandle = "service-catalog";
  const subCollection = collection[getRouteIndex(shopifyNavigationHandle)];
  const sortedCollection = React.useMemo(() => subCollection, [collection]);
  const allProducts = sortedCollection
    .map((col) => {
      return col.products.nodes;
    })
    .flat();
  const activeSubscription =
    contract.subscription_id && contract.subscription_status === "active";
  const contractEndDateMoment = moment(contract.end_date).startOf("day");
  const expiringSoon = contractEndDateMoment <= moment().add(1, "M");
  const expired = contract.status?.toLowerCase() === "inactive";
  const daysLeft = activeSubscription
    ? contractEndDateMoment
        .clone()
        .add(1, "d")
        .diff(moment().startOf("day"), "days")
    : contractEndDateMoment.diff(moment().startOf("day"), "days");
  const isScheduled = contract.status?.toLowerCase() === "scheduled";
  const message = isScheduled
    ? ""
    : contractEndDateMoment < moment()
    ? `Expired ${moment(contract.end_date).startOf("day").format("DD/MM/YYYY")}`
    : activeSubscription
    ? `Renews in ${daysLeft} days`
    : `Expires in ${daysLeft} days`;

  // const isProductAvailable =
  //   allProducts.filter(
  //     (product: any) => product.variants.edges[0].node.sku === contract.sku
  //   ).length > 0;

  return (
    <>
      <div className="contract-status-card" key={contract.ID}>
        <div className="name">{contract.contract_name}</div>
        <div className="status-days">
          {expired ? (
            <div className="red-box">{contract.status.toUpperCase()}</div>
          ) : expiringSoon ? (
            <div className="yellow-box">
              {activeSubscription ? "RENEWING SOON" : "EXPIRING SOON"}
            </div>
          ) : isScheduled ? (
            <div className="yellow-box">{contract.status.toUpperCase()}</div>
          ) : (
            <div className="green-box">ACTIVE</div>
          )}
          <p>{message}</p>
          <Button
            variant="secondary"
            icon={
              <Iconography
                icon="ellipsisVertical"
                size={IconographySizes.Default}
              />
            }
            onClick={() => {
              setDetailModalOpen(true);
            }}
          />
        </div>
      </div>
      {detailModalOpen && (
        <ServiceContractDetailModal
          serviceContractDetail={contract}
          setDetailModalOpen={setDetailModalOpen}
        />
      )}
    </>
  );
};

export default TabContentContractStatus;
